import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, Session } from '../utils/auth';

type AuthContextValue = {
    session: Session | null;
    loading: boolean;
    refresh: () => Promise<void>;
    setLoadingState: (value: boolean) => void;
};

const AuthContext = createContext<AuthContextValue>({ session: null, loading: true, refresh: async () => undefined, setLoadingState: () => undefined });

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);

    const load = async (): Promise<void> => {
    try {
        await auth.refreshTokens();
    } catch (error) {
        // No refresh token cookie is expected on public pages; treat as guest instead of erroring
    } finally {
        // Always resolve the session (returns null/guest state when unauthenticated)
        setSession(await auth.getSession());
        setLoading(false);
    }
};

    useEffect(() => {
        // Skip initial load on SSO callback routes (they call refresh() after token exchange)
        // and on public pages that don't require a session, to avoid refresh errors with no token cookie
        const isOnSSOCallback = window.location.pathname === '/sso/callback' || 
                               window.location.pathname === '/sso/logout/callback';
        const isOnPublicRoute = window.location.pathname === '/dues-estimator';

        if (!isOnSSOCallback && !isOnPublicRoute) {
            load();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refresh = async (): Promise<void> => {
        setLoading(true);
        await load();
    };

    const setLoadingState = (value: boolean): void => {
        setLoading(value);
    };

    return <AuthContext.Provider value={{ session, loading, refresh, setLoadingState }}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
    return useContext(AuthContext);
};


